<script setup lang="ts">
import '@/assets/scss/form.scss';
import AccountPicker from '@/onenetwork/components/account_picker/AccountPicker.vue';
import AttachmentList from '@/onenetwork/components/attachment_list/AttachmentList.vue';
import CSInputText from '@/onenetwork/components/form/CSInputText.vue';
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue';
import RegionPicker from '@/onenetwork/components/region_picker/RegionPicker.vue';
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant';
import type { FormSubmitEvent } from '@primevue/forms';
import { yupResolver } from '@primevue/forms/resolvers/yup';
import { Select, useConfirm } from 'primevue';
import { useToast } from 'primevue/usetoast';
import { computed, nextTick, ref, toRaw, watch } from 'vue'
import BrokerAPI from '../constants/broker.api.uri.const';
import { initialValuesBroker, parseFormDataBroker } from '../helper/form';
import { BROKER_LEVEL_ARRAY, BUSINESS_TYPE_ARRAY } from '../helper/table';
import { schemaUpdateBroker } from '../helper/validationBrokerSchema';
import { useBrokerDetailStore } from '../stores/broker.detail.store';
import TagCell from '@/onenetwork/components/datatable/TagCell.vue';
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue';
import { Icon } from '@iconify/vue/dist/iconify.js';
import RegionSelectComponent from '@/modules/broker/components/RegionSelectComponent.vue'

interface ImageUploadResult {
  uuid: string;
}

interface Props {
  account: any,
  isUpdate?: boolean
}

const confirm = useConfirm();
const BrokerDetailStore = useBrokerDetailStore()
const editable = ref(false)
const props = defineProps({
  account: {},
  isUpdate: {
    type: Boolean,
    required: false,
    default: false
  }
})

const toast = useToast();
const formRef = ref();
const loading = ref(false)
const selectedRegion = ref([])
const selectedDistrict = ref([])
const selectedWard = ref([])
const initialValues = ref(initialValuesBroker(props.account));

const resolver = yupResolver(schemaUpdateBroker);
const updateBroker = async (data: any) => {
  loading.value = true
  try {
    const res = await APIClient.put(BrokerAPI.broker.detail.replace(':id', data.id.toString()), data);
    if (res.data.code === 200) {
      toast.add({ severity: 'success', summary: 'Success', detail: 'Cập nhật thành công', life: 3000 });
      // initialValues.value = initialValuesBroker({...props?.account, ...data, ...res.data.data});
      loading.value = false
      // await nextTick(() => {
      //   formRef.value?.reset();
      // })
      editable.value = false
      BrokerDetailStore.fetchBrokerDetail(data.id, 'update')
    }
  } catch (error: any) {
    loading.value = false
    const errors = error?.response?.data?.errors;
    if (errors && Array.isArray(errors)) {
      errors.forEach((err: any) => {
        if (formRef.value?.states && formRef.value.states[err.field]) {
          formRef.value.states[err.field].invalid = true;
          formRef.value.states[err.field].error = { message: err.error };
        } else if (formRef.value?.states) {
          // Khởi tạo state nếu trường chưa có (chưa touched/dirty)
          formRef.value.states[err.field] = {
            invalid: true,
            error: { message: err.error }
          };
        }
      });
    }

    const errorMessage = error instanceof Error ? error.message : 'Đã có lỗi xảy ra';
    toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 });
  }
}

const onFormSubmit = async ({valid, values}: FormSubmitEvent<Record<string, unknown>>) => {
  console.log(values, 'onFormSubmit');

  if (!valid) {
    return;
  }

  confirm.require({
    message: 'Bạn có muốn lưu các chỉnh sửa?',
    header: 'Xác nhận',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Đồng ý'
    },
    accept: () => {
      console.log(initialValues.value, 'initialValues');
      console.log(values, 'values');
      const formData = parseFormDataBroker({...initialValues.value, ...values});
      formData.regions = _regions.value?.length > 0 ? _regions.value.map((item: any) => item.id) : null;
      console.log(formData, 'formData');
      updateBroker(formData);
    },
    reject: () => {
    }
  });
};

watch(() => props.account, () => {
  initialValues.value = initialValuesBroker(props.account)
}, {immediate: true, deep: true})

const handleRegionChange = (value: any) => {
  console.log('Region changed:', value);
  if (value[0]?.id) {
    initialValues.value.province_id = value[0]?.id
    formRef.value?.setFieldValue('province_id', value[0]?.id)
  } else {
    initialValues.value.province_id = null
    formRef.value?.setFieldValue('province_id', null)
  }
  selectedDistrict.value = [];
  selectedWard.value = [];
}

const handleDistrictChange = (value: any) => {
  console.log('District changed:', value);
  if (value[0]?.id) {
    initialValues.value.district_id = value[0]?.id
    formRef.value?.setFieldValue('district_id', value[0]?.id)
  } else {
    initialValues.value.district_id = null
    formRef.value?.setFieldValue('district_id', null)
  }
  selectedWard.value = [];
}

const handleWardChange = (value: any) => {
  console.log('Ward changed:', value);
  if (value[0]?.id) {
    initialValues.value.ward_id = value[0]?.id
    formRef.value?.setFieldValue('ward_id', value[0]?.id)
  } else {
    initialValues.value.ward_id = null
    formRef.value?.setFieldValue('ward_id', null)
  }
}


watch(() => formRef.value?.values, (newValues) => {
  console.log('Form values changed:', newValues);
}, { deep: true });

const handleUpdateAttachment = (data: any) => {
  const uuid = (data?.attachmentIds || []).map((item: any) => item.uuid);
  formRef.value?.setFieldValue('broker_certificate', uuid);
  formRef.value?.setFieldValue('certificates', data?.attachmentIds);
  initialValues.value.certificates = data?.attachmentIds
}

const cancelEdit = () => {
  editable.value = false
  initialValues.value.province_id = props.account?.user?.province?.id
  initialValues.value.district_id = props.account?.user?.district?.id
  initialValues.value.ward_id = props.account?.user?.ward?.id
  initialValues.value.manager_code = props.account?.broker_relation?.manager?.code || null
  initialValues.value.certificates = props.account?.certificates || []
  formRef.value?.reset()

}

const updateManager = (e: any) => {
  if(e[0]?.id) {
    console.log(e[0], 'e[0]');
    formRef.value?.setFieldValue('manager_code', e[0]?.code);
    formRef.value?.setFieldValue('manager_id', e[0]?.id);
    initialValues.value.manager = {
      id: e[0]?.id,
      full_name: e[0]?.name,
      avatar: {
        url: {
          thumbnail: e[0]?.avt
        }
      },
      phone_number: e[0]?.phone,
      code: e[0]?.code,
      customer_profile: e[0]?.customer,
      broker_profile: e[0]?.broker,
      select: e[0]?.select
    }

  } else {
    formRef.value?.setFieldValue('manager_code', null);
    formRef.value?.setFieldValue('manager_id', null);
    initialValues.value.manager = null
  }
}


const _regions = ref<any[]>([])
watch(
  () => initialValues.value.regions,
  (regions) => {
    _regions.value = Array.isArray(regions) ? regions : []
  },
  { immediate: true, deep: true }
)

const handleSelectRegion = (value: any) => {
  const current = Array.isArray(toRaw(_regions.value)) ? toRaw(_regions.value) : []
  const picked = Array.isArray(value) ? toRaw(value) : value != null ? [toRaw(value)] : []
  if (!picked.length) return
  _regions.value = [...current, ...picked]
}
const removeRegion = (value: any) => {
  console.log('Remove region:', value);
  if(value) {
    _regions.value = _regions.value.filter((item: any) => item.id !== value)
  }
}


</script>

<template>
  <Form
    v-slot="$form"
    ref="formRef"
    :initial-values="initialValues"
    :resolver="resolver"
    @submit="onFormSubmit"
  >
  <div class="flex justify-between mb-5" v-if="isUpdate">
    <div></div>
    <AllowPerm perm="broker.edit_info" action="hide">
      <div class="flex gap-2">
        <CsButton
          type="submit"
          text="Lưu chỉnh sửa"
          :loading="loading"
          :disabled="loading"
          :visible="editable"
        />
        <CsButton
          type="button"
          icon="ri:edit-2-fill"
          text="Chỉnh sửa"
          :visible="!editable"
          @click="editable = true"
        />
        <CsButton
          type="button"
          icon="ri:close-circle-fill"
          text="Huỷ chỉnh sửa"
          variant="danger"
          :visible="editable"
          @click="cancelEdit"
        />
      </div>
    </AllowPerm>
  </div>
    <InputText name="profile_image" type="text" hidden class="form-control" />
    <InputText name="province_id" type="text" hidden class="form-control" />
    <InputText name="district_id" type="text" hidden class="form-control" />
    <InputText name="ward_id" type="text" hidden class="form-control" />
    <InputText name="manager_code"  type="text" hidden class="form-control" />
    <InputText name="manager_id" type="text" hidden class="form-control" />
    <InputText name="supervisor_id" type="text" hidden class="form-control" />
    <InputText name="address" type="text" hidden class="form-control" />
    <InputText name="broker_certificate" type="text" hidden class="form-control" />
    <InputText name="certificates" type="text" hidden class="form-control" />
    <div class="flex flex-col md:flex-row gap-10 items-start" v-if="account?.id">
      <div class="w-full md:w-[240px]">
        <image-picker
          readonly
          height="150px"
          type="photo"
          :src="initialValues.avatar"
        />
      </div>
      <div class="profile w-full sm:flex sm:flex-col md:grid md:grid-cols-3 gap-3">
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Mã code:</label>
             <div class="flex-1">
               <CSInputText name="code" type="text" class="form-control" disabled />
             </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Họ và tên:</label>
            <div class="flex-1">
              <InputText name="full_name" type="text" class="form-control" disabled/>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Số điện thoại:</label>
            <div class="flex-1">
              <InputText name="phone_number" type="text" class="form-control" disabled/>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Email:</label>
            <div class="flex-1">
              <InputText name="email" type="text" class="form-control" disabled/>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Chứng chỉ MG:</label>
            <div class="flex-1">
              <span class="font-bold !text-[10px] text-[#333]">
                <attachment-list :data="initialValues?.certificates" :editable="editable" @update="handleUpdateAttachment" />
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Ngày đăng ký MG:</label>
            <div class="flex-1">
              <InputText name="request_processed_at" type="text" class="form-control" disabled/>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Trạng thái:</label>
            <div class="flex-1">
              <TagCell :data="initialValues?.processing_status" :cellConfig="{}" />
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Khu vực:</label>
            <div class="flex-1">
              <template v-if="editable">
                <RegionSelectComponent :regions="initialValues?.regions" @selected="handleSelectRegion"/>
                <hr/>
                <template v-for="(w, k) in _regions" :key="`ward_item_${k}`">
                  <div class="ward_item">{{w.name}}, {{w.level1.name}} <span @click="()=>removeRegion(w.id)"><Icon icon="material-symbols:close" /></span></div>
                </template>
              </template>
              <template v-else>
                <template v-for="(w, k) in _regions" :key="`ward_item_${k}`">
                  <div class="ward_item">{{w.name}}, {{w.level1.name}}</div>
                </template>
              </template>
            </div>
          </div>

          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Cấp trên trực tiếp:</label>
            <div class="flex-1" >

              <template v-if="editable">
                <account-picker
                  :readOnly="!editable"
                  :account="initialValues?.manager"
                  type="broker"
                  :multiple="false"
                  @update:selected="updateManager"
                />
              </template>
              <template v-else>
                <template v-if="initialValues?.manager">
                  <InputText name="manager_name" :value="`${initialValues?.manager?.code} - ${initialValues?.manager?.full_name}`" type="text" class="form-control" disabled/>
                </template>
                <template v-else>
                  --
                </template>
              </template>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Giám đốc vùng:</label>
            <div class="flex-1">
              <template v-if="initialValues?.manager">
                <InputText name="supervisor_name" :value="`${initialValues?.supervisor?.code} - ${initialValues?.supervisor?.full_name}`" type="text" class="form-control" disabled/>
              </template>
              <template v-else>
                --
              </template>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Loại hình:</label>
            <div class="flex-1">
              <Select
                :disabled="!editable"
                name="business_type"
                :options="BUSINESS_TYPE_ARRAY"
                optionLabel="name"
                placeholder="Chọn loại môi giới"
                class="form-control"
              />

              <span class="italic text-[12px] text-red-500" v-if="$form.business_type?.invalid && editable">
                {{ $form.business_type.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Cấp độ:</label>
            <div class="flex-1">
              <Select
                :disabled="!editable"
                name="broker_level"
                :options="BROKER_LEVEL_ARRAY"
                optionLabel="name"
                placeholder="Chọn cấp độ môi giới"
                class="form-control"
              />
              <span class="italic text-[12px] text-red-500" v-if="$form.broker_level?.invalid && editable">
                {{ $form.broker_level.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Điểm đánh giá:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.point || 0" :editable="!editable" name="point" type="number" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.point?.invalid && editable">
                {{ $form.point.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Mã số thuế:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.tax_id" :editable="!editable" name="tax_id" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.tax_id?.invalid && editable">
                {{ $form.tax_id.error.message }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Ngân hàng:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.bank_name" :editable="!editable" name="bank_name" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.bank_name?.invalid && editable">
                {{ $form.bank_name.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Số tài khoản:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.bank_account_number" :editable="!editable" name="bank_account_number" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.bank_account_number?.invalid && editable">
                {{ $form.bank_account_number.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Chủ tài khoản:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.bank_account_name" :editable="!editable" name="bank_account_name" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.bank_account_name?.invalid && editable">
                {{ $form.bank_account_name.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">Facebook:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.facebook" :editable="!editable" name="facebook" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.facebook?.invalid && editable">
                {{ $form.facebook.error.message }}
              </span>
            </div>
          </div>
          <div class="flex gap-2 mb-1">
            <label class="w-[200px] text-right">zalo:</label>
            <div class="flex-1">
              <CSInputText :model-value="initialValues?.zalo" :editable="!editable" name="zalo" type="text" class="form-control" />
              <span class="italic text-[12px] text-red-500" v-if="$form.zalo?.invalid && editable">
                {{ $form.zalo.error.message }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- <div class="flex justify-end mt-4" v-if="editable">
      <CsButton
        type="submit"
        text="Lưu chỉnh sửa"
        :loading="loading"
        :disabled="loading"
      />
    </div> -->
  </Form>
</template>

<style scoped lang="scss">
.value {
  background: rgba(248, 248, 248, 0.62);
  width: 100%;
  padding: 3px 10px;
}
hr{
  border-top: 1px solid rgb(248, 248, 248);
  padding-top: 10px;
  margin-top: 16px;
}
.ward_item{
  display: flex;
  align-items: center;
  gap: 5px;
  span{
    color: red;
    cursor: pointer;
    &:hover{
      color: #1f2937;
    }
  }
}

.label {
  @apply w-[200px] text-right;
}

.form-control {
  @apply w-full;
}
</style>
